import camelCase from 'lodash/camelCase';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * Legacy Atomic Pricing exposed redirect-URL template fields in camelCase
 * (e.g. `{{price.billingSystemId}}`), whereas Growth's data model is snake_case
 * (e.g. `{{price.billing_system_id}}`). Merchants' custom redirect URLs are
 * copied verbatim during AP -> Growth migration, so those camelCase
 * placeholders resolve to empty strings and break checkout.
 *
 * This returns a deep copy of the data with camelCase aliases added alongside
 * every snake_case key (originals are preserved so Growth-native snake_case
 * templates keep working). A WeakMap cache keeps shared references consistent
 * and prevents infinite recursion on cyclic structures. The input is never
 * mutated, so live React state passed in stays untouched.
 */
export const withCamelCaseAliases = <T>(
  value: T,
  cache: WeakMap<object, unknown> = new WeakMap()
): T => {
  if (Array.isArray(value)) {
    const cached = cache.get(value);
    if (cached) {
      return cached as T;
    }
    const result: unknown[] = [];
    cache.set(value, result);
    value.forEach((entry) => result.push(withCamelCaseAliases(entry, cache)));
    return result as unknown as T;
  }

  if (isPlainObject(value)) {
    const cached = cache.get(value);
    if (cached) {
      return cached as T;
    }
    const result: Record<string, unknown> = {};
    cache.set(value, result);
    Object.entries(value).forEach(([key, val]) => {
      const aliasedVal = withCamelCaseAliases(val, cache);
      result[key] = aliasedVal;
      const camelKey = camelCase(key);
      // Only add the alias when it differs and doesn't collide with a real key.
      if (camelKey && camelKey !== key && !(camelKey in value)) {
        result[camelKey] = aliasedVal;
      }
    });
    return result as T;
  }

  return value;
};
